import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface RelativeTimeProps {
  dateTime: Date;
  options?: Intl.RelativeTimeFormatOptions;
}

const second = 1000;
const minute = second * 60;
const hour = minute * 60;
const day = hour * 24;
const month = day * 30;
const year = day * 365;

const useRelativeTime = (
  time: number,
  locale: string,
  dateNow = Date.now,
  options: Intl.RelativeTimeFormatOptions = {
    style: "short",
    numeric: "auto",
  },
) => {
  const [now, setNow] = useState(dateNow);
  const { t } = useTranslation();

  useEffect(() => {
    if (now - time < minute) {
      const interval = setInterval(() => setNow(dateNow()), second);
      return () => clearInterval(interval);
    }
    if (now - time < hour) {
      const interval = setInterval(() => setNow(dateNow()), minute);
      return () => clearInterval(interval);
    }
  }, [dateNow, now, time]);

  const formatter = useMemo(() => {
    return new Intl.RelativeTimeFormat(locale, options);
  }, [locale, options]);

  return useMemo(() => {
    const relativeTime = now - time;
    const elapsed = Math.abs(relativeTime);

    const sign = Math.sign(-relativeTime);
    if (elapsed < minute) {
      return t("date.now");
    } else if (elapsed < hour) {
      return formatter.format(sign * Math.round(elapsed / minute), "minute");
    } else if (elapsed < day) {
      return formatter.format(sign * Math.round(elapsed / hour), "hour");
    } else if (elapsed < month) {
      return formatter.format(sign * Math.round(elapsed / day), "day");
    } else if (elapsed < year) {
      return formatter.format(sign * Math.round(elapsed / month), "month");
    } else {
      return formatter.format(sign * Math.round(elapsed / year), "year");
    }
  }, [now, time, formatter, t]);
};

export const RelativeTime: FC<RelativeTimeProps> = ({ dateTime, options }) => {
  const { i18n } = useTranslation();

  const relativeTime = useRelativeTime(
    dateTime.getTime(),
    i18n.language.replace("_", "-"),
    Date.now,
    options,
  );

  return <>{relativeTime}</>;
};

RelativeTime.displayName = "RelativeTime";
